import { useCallback, useEffect, useState } from "react";

import useQueryClient from "../api/query";
import type { AgentConfigResponse, AppConfig } from "../api/query/queryClient";
import { useConfig } from "./config";

export const FEATURE_FLAG_KEYS = {
  BANK_RECONCILIATION: "bankReconciliation",
  REPORTS: "reports",
  INSURANCE_VERIFICATION: "insuranceVerification",
  BANK_CONNECTIONS: "bankConnections",
  DENISE: "isDeniseEnabled",
  INSURANCE_AR: "insuranceAr",
} as const;

export type FeatureFlagKey = keyof typeof FEATURE_FLAG_KEYS;
export type FeatureFlagValue = (typeof FEATURE_FLAG_KEYS)[FeatureFlagKey];

type FeatureFlagsState = {
  featureFlags: Record<FeatureFlagValue, boolean>;
  isLoading: boolean;
  error: Error | null;
};

type AgentConfigsState = {
  agentConfigs: AgentConfigResponse[];
  isLoading: boolean;
};

let agentConfigsCache: AgentConfigsState | null = null;
let agentConfigsFetchPromise: Promise<AgentConfigsState> | null = null;

function useAgentConfigs(): AgentConfigsState {
  const queryClient = useQueryClient();
  const [state, setState] = useState<AgentConfigsState>(
    () => agentConfigsCache || { agentConfigs: [], isLoading: true },
  );

  const fetchAgentConfigs = useCallback(async () => {
    if (agentConfigsFetchPromise) {
      return agentConfigsFetchPromise;
    }

    if (agentConfigsCache) {
      return agentConfigsCache;
    }

    setState((prev) => ({ ...prev, isLoading: true }));

    agentConfigsFetchPromise = queryClient
      .getAgentConfigs()
      .then((agentConfigs) => {
        const newState: AgentConfigsState = { agentConfigs, isLoading: false };
        setState(newState);
        agentConfigsCache = newState;
        return newState;
      })
      .catch((error) => {
        // biome-ignore lint/suspicious/noConsole: intentional browser console output
        console.error("Failed to fetch agent configs:", error);
        const fallback: AgentConfigsState = {
          agentConfigs: [],
          isLoading: false,
        };
        setState(fallback);
        return fallback;
      })
      .finally(() => {
        agentConfigsFetchPromise = null;
      });

    return agentConfigsFetchPromise;
  }, [queryClient]);

  useEffect(() => {
    (async () => {
      const newState = await fetchAgentConfigs();
      setState(newState);
      agentConfigsCache = newState;
    })();
  }, [fetchAgentConfigs]);

  return state;
}

function computeFeatureFlags(
  appFlags: AppConfig["featureFlags"],
  agentConfigs: AgentConfigResponse[],
): Record<FeatureFlagValue, boolean> {
  const byAgent = new Map(agentConfigs.map((c) => [c.agent, c]));
  const monet = byAgent.get("MONET");
  const benny = byAgent.get("BENNY")?.config;
  return {
    ...appFlags,
    // `?? enabled` covers org rows written before the verification_visible
    // split; remove once scripts/backfill_benny_split.py has run in prod.
    insuranceVerification: Boolean(
      benny?.verification_visible ?? benny?.enabled,
    ),
    isDeniseEnabled: Boolean(byAgent.get("DENISE")?.config.enabled),
    bankReconciliation: Boolean(monet?.config.dashboard_visible),
    bankConnections: Boolean(monet?.config.bank_connections_visible),
  };
}

export function useFeatureFlags(): FeatureFlagsState {
  const { config, isLoading: configLoading } = useConfig();
  const { agentConfigs, isLoading: agentConfigsLoading } = useAgentConfigs();
  return {
    featureFlags: computeFeatureFlags(config.featureFlags, agentConfigs),
    isLoading: configLoading || agentConfigsLoading,
    error: null,
  };
}

export function useFeatureFlag(featureKey: FeatureFlagValue): {
  isEnabled: boolean;
  isLoading: boolean;
} {
  const { featureFlags, isLoading } = useFeatureFlags();
  return {
    isEnabled: featureFlags[featureKey] || false,
    isLoading,
  };
}
